import { queryClient } from "./queryClient"
import { isPrerequisiteFailedError } from "./PrerequisiteFailedError"
import { getErrorClassification, isRetryable } from "./MutationError"

/**
 * Retry all mutations that failed due to network errors.
 *
 * Called when the app comes back online. Network-error mutations are
 * retryable without user intervention, unlike validation or auth errors.
 *
 * Skips PrerequisiteFailedError mutations — they auto-resolve via the
 * scopeCascadeRetry subscriber when their parent gating mutation succeeds.
 *
 * If a retried mutation is a gating CREATE and it succeeds, the existing
 * scopeCascadeRetry subscriber will automatically retry any dependent
 * PrerequisiteFailedError mutations in the same scope.
 *
 * Follows the same remove → rebuild → execute pattern as
 * retryFailedMutations.ts and scopeCascadeRetry.ts.
 */
export function retryNetworkFailedMutations(): void {
  const mutations = queryClient.getMutationCache().findAll({
    predicate: (mutation) => {
      if (mutation.state.status !== "error") return false
      if (isPrerequisiteFailedError(mutation.state.error)) return false

      const classification = getErrorClassification(mutation.state.error)
      return isRetryable(classification)
    },
  })

  for (const mutation of mutations) {
    const variables = mutation.state.variables

    // Remove the failed mutation from cache
    queryClient.getMutationCache().remove(mutation)

    // Rebuild with defaults (restores mutationFn/onSuccess/onError after rehydration)
    const rebuilt = queryClient.getMutationCache().build(queryClient, {
      ...queryClient.defaultMutationOptions(mutation.options),
      ...mutation.options,
    })

    // Execute with original variables
    rebuilt.execute(variables)
  }
}
